import { Component, OnDestroy } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { App } from '@capacitor/app';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { TranslateService } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { register } from 'swiper/element/bundle';
import { AnalyticsService } from './services/analytics.service';
import { AuthService } from './services/authentication.service';
import { JhiEventManager } from './services/event-manager.service';
import { VersionService } from './services/version.service';
import { LoggingService } from './shared/logging.service';

register();

const LANGUE_PAR_DEFAUT = 'fr';
const LANGUES_TRADUITES: readonly string[] = [LANGUE_PAR_DEFAUT];

@Component({
  selector: 'in2-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  private subs = new SubSink();

  constructor(
    private readonly translateService: TranslateService,
    private readonly versionService: VersionService,
    private readonly log: LoggingService,
    private readonly eventManager: JhiEventManager,
    private readonly analyticsService: AnalyticsService,
    private readonly authService: AuthService
  ) {
    this.initializeApp();
    this.setupAppStateListener();
  }

  private setupAppStateListener() {
    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.log.info('Application passée en premier plan');
          this.eventManager.broadcast({ name: 'appStateChange', content: { isActive: true } });
        }
      });
    }
  }

  public async initializeApp() {
    this.translateService.setFallbackLang(LANGUE_PAR_DEFAUT);
    this.translateService.use(this.langueDuTelephoneSiTraduite());

    if (Capacitor.isNativePlatform()) {
      // `setAccessoryBarVisible` n'existe que sur iOS : appelé sur Android, il lève une
      // CapacitorException "not implemented" que le gestionnaire global remonte à Crashlytics.
      if (Capacitor.getPlatform() === 'ios') {
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      }
      // TODO: récupérer la version à partir du package.json
      await this.versionService.fetch();
      FirebaseCrashlytics.setEnabled({ enabled: true });
      // L'en-tête de l'application est un dégradé sombre et le contenu y est blanc : le contenu
      // des barres système reste clair. Leur fond n'a plus d'API native — la webview couvre tout
      // l'écran, c'est le dégradé de l'en-tête qui peint la zone de statut.
      await SystemBars.setStyle({ style: SystemBarsStyle.Dark });

      this.analyticsService.setEnabled(true);
      this.authService.onUserChange().subscribe(user => {
        this.analyticsService.setUserId(user?.id as string);
      });

      this.versionService.fetch().then(v => this.analyticsService.setUserProperty('version', v));
      this.analyticsService.trackPageChanges();

      // this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
    }
  }

  public ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private langueDuTelephoneSiTraduite(): string {
    const langueDuTelephone = this.translateService.getBrowserLang();
    return langueDuTelephone && LANGUES_TRADUITES.includes(langueDuTelephone) ? langueDuTelephone : LANGUE_PAR_DEFAUT;
  }
}
